import { PingOnboardingPanel } from '../components/PingOnboardingPanel';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { PaymentProviderDebugSettings } from '../components/PaymentProviderDebugSettings';
import { PingSandboxCheck } from '../components/PingSandboxCheck';
import {
  adminApi,
  AdyenRuntimeStatus,
  AdyenWebhookConfigStatus
} from '../services/api';

type BankIdProvider = 'simulated' | 'tic_test' | 'bankid_prod';
type BankIdAssurance = 'simulated' | 'test' | 'production';

type DebugSettings = {
  nodeEnv: string;
  debugConfigurable: boolean;
  emailVerificationEnabled: boolean;
  notificationEmailEnabled: boolean;
  bankidOnboardingProvider: BankIdProvider;
  bankidRequiredAssurance: BankIdAssurance;
};

const defaultSettings: DebugSettings = {
  nodeEnv: 'development',
  debugConfigurable: false,
  emailVerificationEnabled: true,
  notificationEmailEnabled: true,
  bankidOnboardingProvider: 'simulated',
  bankidRequiredAssurance: 'simulated'
};

const defaultAdyenRuntime: AdyenRuntimeStatus = {
  requestedMode: 'mock',
  effectiveMode: 'mock',
  checkoutEnvironment: 'test',
  cardPaymentsEnabled: true,
  debugConfigurable: false,
  canUseSandbox: false,
  sandboxConfirmation: 'ENABLE_ADYEN_SANDBOX',
  reasons: [],
  gates: {
    nodeEnv: 'development',
    envMode: 'mock',
    forceMock: false,
    allowSandboxApiInTest: false,
    adminSelectedSandbox: false
  },
  credentials: {
    balancePlatformApiKeyConfigured: false,
    legalEntityManagementApiKeyConfigured: false,
    checkoutApiKeyConfigured: false,
    merchantAccountConfigured: false,
    platformLiableBalanceAccountConfigured: false,
    checkoutClientKeyConfigured: false
  },
  endpoints: {
    legalEntityManagementApiBaseUrl: '',
    balancePlatformApiBaseUrl: '',
    checkoutApiBaseUrl: '',
    transfersApiBaseUrl: ''
  }
};

const defaultAdyenWebhookStatus: AdyenWebhookConfigStatus = {
  targetUrl: null,
  etappioBaseUrlConfigured: false,
  publicApiBaseUrlConfigured: false,
  managementApiConfigured: false,
  merchantIdConfigured: false,
  companyIdConfigured: false,
  selectedScope: null,
  existingWebhooks: [],
  matchingWebhook: null,
  recommendedActions: [],
  confirmations: {
    configure: 'CONFIGURE_ADYEN_WEBHOOKS',
    test: 'TEST_ADYEN_WEBHOOK'
  }
};

export function AdminDebugSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<DebugSettings>(defaultSettings);
  const [adyenRuntime, setAdyenRuntime] = useState<AdyenRuntimeStatus>(defaultAdyenRuntime);
  const [adyenWebhookStatus, setAdyenWebhookStatus] = useState<AdyenWebhookConfigStatus>(defaultAdyenWebhookStatus);
  const [adyenWebhookConfigureConfirmation, setAdyenWebhookConfigureConfirmation] = useState('');
  const [adyenWebhookTestConfirmation, setAdyenWebhookTestConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfiguringAdyenWebhook, setIsConfiguringAdyenWebhook] = useState(false);
  const [isTestingAdyenWebhook, setIsTestingAdyenWebhook] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    Promise.all([
      adminApi.getDebugSettings(),
      adminApi.getAdyenRuntime(),
      adminApi.getAdyenWebhookConfigStatus()
    ])
      .then(([debugSettings, runtime, webhookStatus]) => {
        setSettings({ ...defaultSettings, ...debugSettings });
        setAdyenRuntime(runtime);
        setAdyenWebhookStatus(webhookStatus);
      })
      .catch(() => setSaveMessage({ type: 'error', text: t('adminDebugSettings.errors.load') }))
      .finally(() => setIsLoading(false));
  }, [t]);

  const update = <K extends keyof DebugSettings>(key: K, value: DebugSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const saved = await adminApi.updateDebugSettings({
        emailVerificationEnabled: settings.emailVerificationEnabled,
        notificationEmailEnabled: settings.notificationEmailEnabled,
        bankidOnboardingProvider: settings.bankidOnboardingProvider,
        bankidRequiredAssurance: settings.bankidRequiredAssurance
      });
      setSettings(saved);
      setSaveMessage({ type: 'success', text: t('adminDebugSettings.toasts.saved') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage({ type: 'error', text: t('adminDebugSettings.errors.save') });
    } finally {
      setIsSaving(false);
    }
  };

  const refreshAdyenWebhookStatus = async () => {
    const webhookStatus = await adminApi.getAdyenWebhookConfigStatus();
    setAdyenWebhookStatus(webhookStatus);
    return webhookStatus;
  };

  const handleConfigureAdyenWebhook = async () => {
    setIsConfiguringAdyenWebhook(true);
    setSaveMessage(null);
    try {
      const result = await adminApi.applyAdyenWebhookConfig(adyenWebhookConfigureConfirmation);
      await refreshAdyenWebhookStatus();
      setAdyenWebhookConfigureConfirmation('');
      setSaveMessage({
        type: 'success',
        text: result.hmac.generated
          ? t('adminDebugSettings.toasts.webhookConfiguredHmac')
          : t('adminDebugSettings.toasts.webhookConfigured')
      });
    } catch {
      setSaveMessage({ type: 'error', text: t('adminDebugSettings.errors.configureWebhook') });
    } finally {
      setIsConfiguringAdyenWebhook(false);
    }
  };

  const handleTestAdyenWebhook = async () => {
    setIsTestingAdyenWebhook(true);
    setSaveMessage(null);
    try {
      const result = await adminApi.testAdyenWebhookConfig(adyenWebhookTestConfirmation);
      await refreshAdyenWebhookStatus();
      setAdyenWebhookTestConfirmation('');
      const successCount = result.result.filter(row => row.status === 'success').length;
      setSaveMessage({
        type: successCount > 0 ? 'success' : 'error',
        text: successCount > 0
          ? t('adminDebugSettings.toasts.webhookTested')
          : t('adminDebugSettings.toasts.webhookTestNoSuccess')
      });
    } catch {
      setSaveMessage({ type: 'error', text: t('adminDebugSettings.errors.testWebhook') });
    } finally {
      setIsTestingAdyenWebhook(false);
    }
  };

  const disabled = !settings.debugConfigurable;
  const adyenRuntimeDisabled = disabled || !adyenRuntime.debugConfigurable;
  const adyenWebhookBaseUrlConfigured =
    adyenWebhookStatus.etappioBaseUrlConfigured ?? adyenWebhookStatus.publicApiBaseUrlConfigured;
  const adyenWebhookDisabled =
    disabled ||
    !adyenWebhookBaseUrlConfigured ||
    !adyenWebhookStatus.managementApiConfigured ||
    (!adyenWebhookStatus.merchantIdConfigured && !adyenWebhookStatus.companyIdConfigured);
  const adyenWebhookConfigureConfirmed =
    adyenWebhookConfigureConfirmation === adyenWebhookStatus.confirmations.configure;
  const adyenWebhookTestConfirmed =
    adyenWebhookTestConfirmation === adyenWebhookStatus.confirmations.test;

  return (
    <DashboardLayout>
      <Breadcrumb items={[
        { label: t('adminDebugSettings.breadcrumb.system'), path: '/admin/system-menu' },
        { label: t('adminDebugSettings.breadcrumb.current') }
      ]} />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">{t('adminDebugSettings.title')}</h4>
          <div className="text-muted small">{t('adminDebugSettings.environment', { nodeEnv: settings.nodeEnv })}</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {saveMessage && (
            <span className={`text-${saveMessage.type === 'success' ? 'success' : 'danger'} small`}>
              {saveMessage.text}
            </span>
          )}
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={disabled || isSaving || isLoading}
          >
            {isSaving ? t('adminDebugSettings.actions.saving') : t('adminDebugSettings.actions.save')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-5"><div className="spinner-border" role="status" /></div>
      ) : (
        <div className="d-flex flex-column gap-4">
          <Card className={disabled ? 'opacity-50' : ''}>
            {disabled && (
              <div className="alert alert-secondary" role="alert">
                {t('adminDebugSettings.lockedInProduction')}
              </div>
            )}

            <div className="form-check form-switch mb-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="emailVerificationEnabled"
                checked={settings.emailVerificationEnabled}
                disabled={disabled}
                onChange={event => update('emailVerificationEnabled', event.target.checked)}
              />
              <label className="form-check-label fw-semibold" htmlFor="emailVerificationEnabled">
                {t('adminDebugSettings.fields.emailVerification.label')}
              </label>
              <div className="form-text">{t('adminDebugSettings.fields.emailVerification.help')}</div>
            </div>

            <div className="form-check form-switch mb-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="notificationEmailEnabled"
                checked={settings.notificationEmailEnabled}
                disabled={disabled}
                onChange={event => update('notificationEmailEnabled', event.target.checked)}
              />
              <label className="form-check-label fw-semibold" htmlFor="notificationEmailEnabled">
                {t('adminDebugSettings.fields.notificationEmail.label')}
              </label>
              <div className="form-text">{t('adminDebugSettings.fields.notificationEmail.help')}</div>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold" htmlFor="bankidOnboardingProvider">{t('adminDebugSettings.fields.bankidProvider.label')}</label>
                <select
                  id="bankidOnboardingProvider"
                  className="form-select"
                  value={settings.bankidOnboardingProvider}
                  disabled={disabled}
                  onChange={event => update('bankidOnboardingProvider', event.target.value as BankIdProvider)}
                >
                  <option value="simulated">{t('adminDebugSettings.options.bankidProvider.simulated')}</option>
                  <option value="tic_test">{t('adminDebugSettings.options.bankidProvider.ticTest')}</option>
                  <option value="bankid_prod">{t('adminDebugSettings.options.bankidProvider.bankidProd')}</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold" htmlFor="bankidRequiredAssurance">{t('adminDebugSettings.fields.bankidAssurance.label')}</label>
                <select
                  id="bankidRequiredAssurance"
                  className="form-select"
                  value={settings.bankidRequiredAssurance}
                  disabled={disabled}
                  onChange={event => update('bankidRequiredAssurance', event.target.value as BankIdAssurance)}
                >
                  <option value="simulated">{t('adminDebugSettings.options.bankidAssurance.simulated')}</option>
                  <option value="test">{t('adminDebugSettings.options.bankidAssurance.test')}</option>
                  <option value="production">{t('adminDebugSettings.options.bankidAssurance.production')}</option>
                </select>
              </div>
            </div>
          </Card>

          <Card>
            <PaymentProviderDebugSettings onSaved={async () => {
              try { setAdyenRuntime(await adminApi.getAdyenRuntime()); }
              catch { setSaveMessage({ type: 'error', text: t('adminDebugSettings.errors.load') }); }
            }} />
          </Card>

          <Card>
            <PingSandboxCheck enabled={settings.debugConfigurable} />
            {settings.debugConfigurable && <div className="mt-4"><PingOnboardingPanel fee /></div>}
          </Card>

          <Card className={adyenRuntimeDisabled ? 'opacity-50' : ''}>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 className="mb-1">{t('adminDebugSettings.adyenRuntime.title')}</h5>
                <div className="text-muted small">
                  {t('adminDebugSettings.adyenRuntime.activeLabel')} <span className="font-monospace">{adyenRuntime.effectiveMode}</span>{' '}
                  {t('adminDebugSettings.adyenRuntime.selectedLabel')} <span className="font-monospace">{adyenRuntime.requestedMode}</span>
                </div>
              </div>
              <span className={`badge ${adyenRuntime.effectiveMode === 'live' ? 'bg-success' : adyenRuntime.effectiveMode === 'sandbox' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                {adyenRuntime.effectiveMode === 'live' ? t('adminDebugSettings.adyenRuntime.badge.live') : adyenRuntime.effectiveMode === 'sandbox' ? t('adminDebugSettings.adyenRuntime.badge.sandbox') : t('adminDebugSettings.adyenRuntime.badge.mock')}
              </span>
            </div>

            {adyenRuntimeDisabled && (
              <div className="alert alert-secondary" role="alert">
                {t('adminDebugSettings.adyenRuntime.lockedInProduction')}
              </div>
            )}

            {adyenRuntime.reasons.length > 0 && (
              <div className="alert alert-info" role="alert">
                <div className="fw-semibold">{t('adminDebugSettings.adyenRuntime.activeGates')}</div>
                <ul className="mb-0 ps-3">
                  {adyenRuntime.reasons.map(reason => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="row g-3 mt-2">
              {([
                ['Balance Platform API', adyenRuntime.credentials.balancePlatformApiKeyConfigured],
                ['Legal Entity Management API', adyenRuntime.credentials.legalEntityManagementApiKeyConfigured],
                ['Checkout API', adyenRuntime.credentials.checkoutApiKeyConfigured],
                ['Merchant account', adyenRuntime.credentials.merchantAccountConfigured],
                ['Platform balance account', adyenRuntime.credentials.platformLiableBalanceAccountConfigured]
              ] as Array<[string, boolean]>).map(([label, configured]) => (
                <div className="col-md-6 col-xl" key={label}>
                  <div className="border rounded p-2 h-100">
                    <div className="small text-muted">{label}</div>
                    <div className={configured ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                      {configured ? t('adminDebugSettings.status.configured') : t('adminDebugSettings.status.missing')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className={adyenWebhookDisabled ? 'opacity-50' : ''}>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 className="mb-1">{t('adminDebugSettings.adyenWebhook.title')}</h5>
                <div className="text-muted small">
                  {t('adminDebugSettings.adyenWebhook.targetLabel')}{' '}
                  <span className="font-monospace">
                    {adyenWebhookStatus.targetUrl ?? t('adminDebugSettings.adyenWebhook.targetMissing')}
                  </span>
                </div>
              </div>
              <span className={`badge ${adyenWebhookStatus.matchingWebhook ? 'bg-success' : 'bg-secondary'}`}>
                {adyenWebhookStatus.matchingWebhook ? t('adminDebugSettings.adyenWebhook.badge.matches') : t('adminDebugSettings.adyenWebhook.badge.notCreated')}
              </span>
            </div>

            {adyenWebhookStatus.recommendedActions.length > 0 && (
              <div className="alert alert-info" role="alert">
                <div className="fw-semibold">{t('adminDebugSettings.adyenWebhook.nextSteps')}</div>
                <ul className="mb-0 ps-3">
                  {adyenWebhookStatus.recommendedActions.map(action => (
                    <li key={action}>{action}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="row g-3 mb-3">
              {([
                ['Etappio base URL', adyenWebhookBaseUrlConfigured],
                ['Management API', adyenWebhookStatus.managementApiConfigured],
                ['Merchant ID', adyenWebhookStatus.merchantIdConfigured],
                ['Company ID', adyenWebhookStatus.companyIdConfigured],
                ['HMAC', adyenWebhookStatus.matchingWebhook?.hmacKeyConfigured ?? false]
              ] as Array<[string, boolean]>).map(([label, configured]) => (
                <div className="col-md-6 col-xl" key={label}>
                  <div className="border rounded p-2 h-100">
                    <div className="small text-muted">{label}</div>
                    <div className={configured ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                      {configured ? t('adminDebugSettings.status.configured') : t('adminDebugSettings.status.missing')}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {adyenWebhookStatus.matchingWebhook && (
              <div className="border rounded p-3 mb-3">
                <div className="row g-2 small">
                  <div className="col-md-3">
                    <span className="text-muted">{t('adminDebugSettings.adyenWebhook.details.id')}</span>
                    <div className="font-monospace">{adyenWebhookStatus.matchingWebhook.id}</div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-muted">{t('adminDebugSettings.adyenWebhook.details.format')}</span>
                    <div>{adyenWebhookStatus.matchingWebhook.communicationFormat ?? '-'}</div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-muted">{t('adminDebugSettings.adyenWebhook.details.active')}</span>
                    <div>{adyenWebhookStatus.matchingWebhook.active ? t('adminDebugSettings.adyenWebhook.details.yes') : t('adminDebugSettings.adyenWebhook.details.no')}</div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-muted">{t('adminDebugSettings.adyenWebhook.details.errorStatus')}</span>
                    <div>{adyenWebhookStatus.matchingWebhook.hasError ? t('adminDebugSettings.adyenWebhook.details.hasError') : t('adminDebugSettings.adyenWebhook.details.noError')}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="row g-3">
              <div className="col-lg-5">
                <label className="form-label fw-semibold" htmlFor="adyenWebhookConfigureConfirmation">
                  {t('adminDebugSettings.adyenWebhook.configureConfirmationLabel')}
                </label>
                <input
                  id="adyenWebhookConfigureConfirmation"
                  className="form-control"
                  value={adyenWebhookConfigureConfirmation}
                  disabled={adyenWebhookDisabled}
                  placeholder={adyenWebhookStatus.confirmations.configure}
                  onChange={event => setAdyenWebhookConfigureConfirmation(event.target.value)}
                />
                <div className="form-text">{t('adminDebugSettings.help.writeExactly', { value: adyenWebhookStatus.confirmations.configure })}</div>
              </div>
              <div className="col-lg-2 d-flex align-items-end">
                <Button
                  variant="warning"
                  onClick={handleConfigureAdyenWebhook}
                  disabled={adyenWebhookDisabled || isConfiguringAdyenWebhook || !adyenWebhookConfigureConfirmed}
                  isLoading={isConfiguringAdyenWebhook}
                >
                  {t('adminDebugSettings.actions.configure')}
                </Button>
              </div>
              <div className="col-lg-3">
                <label className="form-label fw-semibold" htmlFor="adyenWebhookTestConfirmation">
                  {t('adminDebugSettings.adyenWebhook.testConfirmationLabel')}
                </label>
                <input
                  id="adyenWebhookTestConfirmation"
                  className="form-control"
                  value={adyenWebhookTestConfirmation}
                  disabled={adyenWebhookDisabled || !adyenWebhookStatus.matchingWebhook}
                  placeholder={adyenWebhookStatus.confirmations.test}
                  onChange={event => setAdyenWebhookTestConfirmation(event.target.value)}
                />
                <div className="form-text">{t('adminDebugSettings.help.writeExactly', { value: adyenWebhookStatus.confirmations.test })}</div>
              </div>
              <div className="col-lg-2 d-flex align-items-end">
                <Button
                  variant="secondary"
                  onClick={handleTestAdyenWebhook}
                  disabled={
                    adyenWebhookDisabled ||
                    isTestingAdyenWebhook ||
                    !adyenWebhookStatus.matchingWebhook ||
                    !adyenWebhookTestConfirmed
                  }
                  isLoading={isTestingAdyenWebhook}
                >
                  {t('adminDebugSettings.actions.test')}
                </Button>
              </div>
            </div>
          </Card>

        </div>
      )}
    </DashboardLayout>
  );
}
